import { useState, useEffect, useMemo, useCallback } from "react";
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import {
  getPerformanceChartData,
  getUISettings,
  type StrategySeries,
  type PerformanceTimeRange,
  type BenchmarkPoint,
} from "../../services/portfolioService";

interface PerformanceChartProps {
  investmentIds: string[];
  strategyNames?: Record<string, string>;
}

const timeRanges: { value: PerformanceTimeRange; label: string }[] = [
  { value: "ALL", label: "All Time" },
  { value: "YTD", label: "YTD" },
  { value: "1M", label: "1M" },
  { value: "3M", label: "3M" },
  { value: "6M", label: "6M" },
  { value: "1Y", label: "1Y" },
];

// SPY benchmark color - always pink
const SPY_COLOR = "#f472b6";

function formatPercent(value: number): string {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
}

function formatDate(dateStr: string): string {
  // Use T12:00:00 to avoid UTC midnight shifting to previous day in local timezone
  const date = new Date(dateStr + "T12:00:00");
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatDateFull(dateStr: string): string {
  const date = new Date(dateStr + "T12:00:00");
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Compute EMA (Exponential Moving Average) for a data series
function computeEMA(data: (number | null)[], period: number): (number | null)[] {
  const result: (number | null)[] = [];
  const multiplier = 2 / (period + 1);
  let ema: number | null = null;

  for (let i = 0; i < data.length; i++) {
    const val = data[i];
    if (val === null) {
      result.push(ema);
      continue;
    }
    if (ema === null) {
      ema = val;
    } else {
      ema = (val - ema) * multiplier + ema;
    }
    result.push(ema);
  }
  return result;
}

export function PerformanceChart({ investmentIds }: PerformanceChartProps) {
  // Data state
  const [strategies, setStrategies] = useState<StrategySeries[]>([]);
  const [spyBenchmark, setSpyBenchmark] = useState<BenchmarkPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Expand/collapse state - default collapsed
  const [isExpanded, setIsExpanded] = useState(false);

  // Visibility state - track which strategies are visible (by investment_id)
  const [visibleStrategies, setVisibleStrategies] = useState<Set<string>>(
    new Set()
  );
  const [showSpy, setShowSpy] = useState(true);
  const [visibilityInitialized, setVisibilityInitialized] = useState(false);

  // Time range state
  const [timeRange, setTimeRange] = useState<PerformanceTimeRange>("ALL");
  const [customFrom, setCustomFrom] = useState<string>("");
  const [customTo, setCustomTo] = useState<string>("");
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  // Polling state
  const [refreshInterval, setRefreshInterval] = useState<number>(300); // Default 5 minutes
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Load UI settings on mount
  useEffect(() => {
    async function loadSettings() {
      try {
        const settings = await getUISettings();
        setRefreshInterval(settings.refresh_interval_seconds);
      } catch (e) {
        console.warn("Failed to load UI settings, using defaults", e);
      }
    }
    loadSettings();
  }, []);

  // Fetch performance data
  const fetchData = useCallback(async () => {
    if (investmentIds.length === 0) {
      setStrategies([]);
      setIsLoading(false);
      return;
    }

    try {
      setError(null);
      const data = await getPerformanceChartData(
        investmentIds,
        timeRange,
        customFrom || undefined,
        customTo || undefined
      );

      setStrategies(data.strategies || []);
      setSpyBenchmark(data.spy_benchmark || []);

      // Initialize visibility - show all by default (only once)
      if (!visibilityInitialized && data.strategies?.length > 0) {
        setVisibleStrategies(
          new Set(data.strategies.map((s) => s.investment_id))
        );
        setVisibilityInitialized(true);
      }

      setLastRefresh(new Date());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load performance data");
    } finally {
      setIsLoading(false);
    }
  }, [investmentIds, timeRange, customFrom, customTo, visibilityInitialized]);

  // Initial load
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [fetchData]);

  // Polling with configurable interval
  useEffect(() => {
    if (refreshInterval <= 0) return;

    const interval = setInterval(() => {
      fetchData();
    }, refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [refreshInterval, fetchData]);

  // Toggle strategy visibility
  const toggleVisibility = (investmentId: string) => {
    setVisibleStrategies((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(investmentId)) {
        newSet.delete(investmentId);
      } else {
        newSet.add(investmentId);
      }
      return newSet;
    });
  };

  // Combine all data points into a single array for the chart
  // Each data point has: date, [strategy_id]_return, [strategy_id]_value, spy_return, etc.
  const chartData = useMemo(() => {
    if (strategies.length === 0 && spyBenchmark.length === 0) return [];

    // Get all unique dates across all strategies and SPY
    const allDates = new Set<string>();
    strategies.forEach((s) => {
      s.data_points.forEach((p) => allDates.add(p.date));
    });
    spyBenchmark.forEach((p) => allDates.add(p.date));

    const sortedDates = Array.from(allDates).sort();

    // Build SPY lookup map for fast access
    const spyByDate = new Map<string, BenchmarkPoint>();
    spyBenchmark.forEach((p) => spyByDate.set(p.date, p));

    // Build combined data
    return sortedDates.map((date) => {
      const point: Record<string, string | number | boolean> = { date };
      let isLive = false;

      strategies.forEach((s) => {
        const dataPoint = s.data_points.find((p) => p.date === date);
        if (dataPoint) {
          // Store return % for this strategy
          point[`${s.investment_id}_return`] = parseFloat(
            dataPoint.total_return_pct
          );
          point[`${s.investment_id}_value`] = parseFloat(
            dataPoint.portfolio_value
          );
          // Track if this is a live data point
          if (dataPoint.is_live) {
            isLive = true;
            point[`${s.investment_id}_isLive`] = true;
          }
        }
      });

      // Add SPY benchmark data
      const spyPoint = spyByDate.get(date);
      if (spyPoint) {
        point["spy_return"] = parseFloat(spyPoint.total_return_pct);
        point["spy_value"] = parseFloat(spyPoint.value);
        if (spyPoint.is_live) {
          isLive = true;
          point["spy_isLive"] = true;
        }
      }

      point["isLive"] = isLive;
      return point;
    });
  }, [strategies, spyBenchmark]);

  // Normalize data to show % change from start of period
  const normalizedData = useMemo(() => {
    if (chartData.length === 0) return [];

    // First find the first valid value for each strategy
    // Skip zero-value points (pre-execution snapshots where portfolio_value=0)
    const firstValues: Record<string, number> = {};
    strategies.forEach((s) => {
      const firstPoint = chartData.find(
        (p) =>
          p[`${s.investment_id}_return`] !== undefined &&
          p[`${s.investment_id}_value`] !== undefined &&
          (p[`${s.investment_id}_value`] as number) > 0
      );
      if (firstPoint) {
        firstValues[s.investment_id] = firstPoint[
          `${s.investment_id}_return`
        ] as number;
      }
    });

    // Find first SPY value
    const firstSpyPoint = chartData.find((p) => p["spy_return"] !== undefined);
    const firstSpyValue = firstSpyPoint ? (firstSpyPoint["spy_return"] as number) : 0;

    // Normalize relative to first value
    return chartData.map((point) => {
      const normalized: Record<string, string | number | boolean> = { date: point.date };

      // Preserve live status
      if (point["isLive"]) {
        normalized["isLive"] = true;
      }

      strategies.forEach((s) => {
        const rawVal = point[`${s.investment_id}_return`];
        const portfolioVal = point[`${s.investment_id}_value`] as number;
        if (
          rawVal !== undefined &&
          firstValues[s.investment_id] !== undefined &&
          portfolioVal > 0 // Skip zero-value pre-execution snapshots
        ) {
          // Calculate delta from start value
          normalized[`${s.investment_id}`] =
            (rawVal as number) - firstValues[s.investment_id];
          normalized[`${s.investment_id}_raw`] = rawVal;
          normalized[`${s.investment_id}_value`] =
            point[`${s.investment_id}_value`];
          // Preserve live status for this strategy
          if (point[`${s.investment_id}_isLive`]) {
            normalized[`${s.investment_id}_isLive`] = true;
          }
        }
      });

      // Normalize SPY
      const spyRaw = point["spy_return"];
      if (spyRaw !== undefined) {
        normalized["spy"] = (spyRaw as number) - firstSpyValue;
        normalized["spy_raw"] = spyRaw;
        normalized["spy_value"] = point["spy_value"];
        if (point["spy_isLive"]) {
          normalized["spy_isLive"] = true;
        }
      }

      return normalized;
    });
  }, [chartData, strategies]);

  // Enrich normalized data with EMA lines
  const enrichedData = useMemo(() => {
    if (normalizedData.length === 0) return [];

    const emaPeriod = normalizedData.length > 20 ? 10 : Math.max(3, Math.floor(normalizedData.length / 3));

    // Compute EMA for each visible strategy
    const emaResults: Record<string, (number | null)[]> = {};
    strategies.forEach((s) => {
      const values = normalizedData.map((p) => {
        const v = p[s.investment_id];
        return typeof v === "number" ? v : null;
      });
      emaResults[s.investment_id] = computeEMA(values, emaPeriod);
    });

    // Compute EMA for SPY
    const spyValues = normalizedData.map((p) => {
      const v = p["spy"];
      return typeof v === "number" ? v : null;
    });
    emaResults["spy"] = computeEMA(spyValues, emaPeriod);

    // Merge EMA values into data
    return normalizedData.map((point, i) => {
      const enriched = { ...point };
      strategies.forEach((s) => {
        const emaVal = emaResults[s.investment_id]?.[i];
        if (emaVal !== null && emaVal !== undefined) {
          enriched[`${s.investment_id}_ema`] = emaVal;
        }
      });
      const spyEma = emaResults["spy"]?.[i];
      if (spyEma !== null && spyEma !== undefined) {
        enriched["spy_ema"] = spyEma;
      }
      return enriched;
    });
  }, [normalizedData, strategies]);

  // Period return: the final value for each strategy in the selected range
  const periodReturns = useMemo(() => {
    if (enrichedData.length === 0) return {};
    const lastPoint = enrichedData[enrichedData.length - 1];
    const returns: Record<string, number> = {};
    strategies.forEach((s) => {
      const val = lastPoint[s.investment_id];
      if (typeof val === "number") returns[s.investment_id] = val;
    });
    const spyVal = lastPoint["spy"];
    if (typeof spyVal === "number") returns["spy"] = spyVal;
    return returns;
  }, [enrichedData, strategies]);

  // Check if we have any live data points
  const hasLiveData = useMemo(() => {
    return enrichedData.some((p) => p["isLive"]);
  }, [enrichedData]);

  // Custom tooltip
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderTooltip = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (props: any) => {
      const { active, payload, label } = props;
      if (!active || !payload || payload.length === 0) return null;

      // Check if this is a live data point
      const isLivePoint = payload[0]?.payload?.isLive;

      return (
        <div className="bg-surface-800 border border-surface-600 rounded-lg p-3 shadow-lg">
          <div className="flex items-center gap-2 mb-2">
            <p className="text-surface-300 text-sm">{formatDateFull(label)}</p>
            {isLivePoint && (
              <span className="flex items-center gap-1 text-xs text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                </span>
                Live
              </span>
            )}
          </div>
          {payload
            .filter(
              (entry: { dataKey: string }, idx: number, arr: { dataKey: string }[]) =>
                entry.dataKey &&
                !entry.dataKey.includes("_") &&
                arr.findIndex((e: { dataKey: string }) => e.dataKey === entry.dataKey) === idx
            )
            .map(
            (entry: {
              dataKey: string;
              color: string;
              value: number;
              payload: Record<string, number>;
            }) => {

              // Handle SPY benchmark
              if (entry.dataKey === "spy") {
                const spyValue = entry.payload["spy_value"];
                return (
                  <div
                    key="spy"
                    className="flex items-center gap-2 mb-1"
                  >
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: SPY_COLOR }}
                    />
                    <span className="text-surface-300 text-sm">
                      SPY:
                    </span>
                    <span className="text-surface-200 text-sm font-medium">
                      ${Number(spyValue).toLocaleString()}
                    </span>
                    <span
                      className={`text-sm font-medium ${entry.value >= 0 ? "text-emerald-400" : "text-red-400"}`}
                    >
                      ({formatPercent(entry.value)})
                    </span>
                  </div>
                );
              }

              const strategy = strategies.find(
                (s) => s.investment_id === entry.dataKey
              );
              if (!strategy) return null;

              const portfolioValue = entry.payload[`${entry.dataKey}_value`];

              return (
                <div
                  key={entry.dataKey}
                  className="flex items-center gap-2 mb-1"
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-surface-300 text-sm truncate max-w-[100px]">
                    {strategy.strategy_name}:
                  </span>
                  <span className="text-surface-200 text-sm font-medium">
                    ${Number(portfolioValue).toLocaleString()}
                  </span>
                  <span
                    className={`text-sm font-medium ${entry.value >= 0 ? "text-emerald-400" : "text-red-400"}`}
                  >
                    ({formatPercent(entry.value)})
                  </span>
                </div>
              );
            }
          )}
        </div>
      );
    },
    [strategies]
  );

  // Visible strategies for rendering
  const visibleStrategiesList = strategies.filter((s) =>
    visibleStrategies.has(s.investment_id)
  );

  if (investmentIds.length === 0) {
    return null;
  }

  return (
    <div className="glass-card p-3 sm:p-4">
      {/* Header - clickable to expand/collapse */}
      <div 
        className="flex items-center justify-between cursor-pointer select-none"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          {/* Chevron */}
          <svg
            className={`w-4 h-4 text-surface-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <h3 className="text-sm sm:text-base font-semibold text-surface-100">
            Performance
          </h3>
          {/* Live indicator or last update time */}
          {hasLiveData ? (
            <span className="flex items-center gap-1 text-xs text-emerald-400">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="hidden sm:inline">Live</span>
            </span>
          ) : enrichedData.length > 0 ? (
            <span className="text-xs text-surface-500 hidden sm:inline">
              as of {formatDate(enrichedData[enrichedData.length - 1].date as string)}
            </span>
          ) : (
            <span className="text-xs text-surface-500 hidden sm:inline">
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
        </div>

        {/* Compact summary when collapsed */}
        {!isExpanded && enrichedData.length > 0 && visibleStrategiesList.length > 0 && (
          <div className="flex items-center gap-2 sm:gap-4">
            {visibleStrategiesList.slice(0, 2).map((s) => {
              const value = periodReturns[s.investment_id];
              if (value === undefined) return null;
              return (
                <span
                  key={s.investment_id}
                  className={`text-xs sm:text-sm font-medium ${value >= 0 ? "text-emerald-400" : "text-red-400"}`}
                >
                  {formatPercent(value)}
                </span>
              );
            })}
            {visibleStrategiesList.length > 2 && (
              <span className="text-xs text-surface-500">+{visibleStrategiesList.length - 2}</span>
            )}
          </div>
        )}
      </div>

      {/* Expandable content */}
      {isExpanded && (
        <div className="mt-3">
          {/* Time Range Selector */}
          <div className="flex gap-1 sm:gap-2 overflow-x-auto no-scrollbar pb-1 mb-3">
            {timeRanges.map((range) => (
              <button
                key={range.value}
                onClick={(e) => {
                  e.stopPropagation();
                  setTimeRange(range.value);
                  setShowCustomPicker(false);
                }}
                className={`time-range-btn text-xs sm:text-sm ${
                  timeRange === range.value && !showCustomPicker
                    ? "time-range-btn-active"
                    : "time-range-btn-inactive"
                }`}
              >
                {range.label}
              </button>
            ))}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setTimeRange("CUSTOM");
                setShowCustomPicker(true);
              }}
              className={`time-range-btn text-xs sm:text-sm ${
                showCustomPicker
                  ? "time-range-btn-active"
                  : "time-range-btn-inactive"
              }`}
            >
              Custom
            </button>
          </div>

      {/* Custom Date Picker */}
      {showCustomPicker && (
        <div className="flex flex-wrap gap-2 sm:gap-4 mb-3 items-center">
          <div className="flex items-center gap-2">
            <label className="text-xs sm:text-sm text-surface-400">From:</label>
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="bg-surface-800 border border-surface-600 rounded px-2 py-1 text-xs sm:text-sm text-surface-200"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs sm:text-sm text-surface-400">To:</label>
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="bg-surface-800 border border-surface-600 rounded px-2 py-1 text-xs sm:text-sm text-surface-200"
            />
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); fetchData(); }}
            className="btn btn-primary btn-sm text-xs"
            disabled={!customFrom || !customTo}
          >
            Apply
          </button>
        </div>
      )}

      {/* Strategy Legend with Eye Toggles */}
      {strategies.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {strategies.map((s) => (
            <button
              key={s.investment_id}
              onClick={(e) => { e.stopPropagation(); toggleVisibility(s.investment_id); }}
              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg transition-all ${
                visibleStrategies.has(s.investment_id)
                  ? "bg-surface-700/50 border border-surface-600"
                  : "bg-surface-800/50 border border-surface-700/50 opacity-50"
              }`}
            >
              {/* Eye Icon */}
              {visibleStrategies.has(s.investment_id) ? (
                <svg
                  className="w-3 h-3 sm:w-4 sm:h-4"
                  fill="none"
                  stroke={s.color}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              ) : (
                <svg
                  className="w-3 h-3 sm:w-4 sm:h-4 text-surface-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
              )}
              {/* Color dot */}
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: s.color }}
              />
              {/* Name */}
              <span
                className={`text-xs sm:text-sm truncate max-w-[60px] sm:max-w-[100px] ${
                  visibleStrategies.has(s.investment_id)
                    ? "text-surface-200"
                    : "text-surface-400"
                }`}
              >
                {s.strategy_name}
              </span>
            </button>
          ))}
          {/* SPY benchmark toggle */}
          {spyBenchmark.length > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowSpy(!showSpy); }}
              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg transition-all ${
                showSpy
                  ? "bg-surface-700/50 border border-surface-600"
                  : "bg-surface-800/50 border border-surface-700/50 opacity-50"
              }`}
            >
              {/* Eye Icon */}
              {showSpy ? (
                <svg
                  className="w-3 h-3 sm:w-4 sm:h-4"
                  fill="none"
                  stroke={SPY_COLOR}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              ) : (
                <svg
                  className="w-3 h-3 sm:w-4 sm:h-4 text-surface-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
              )}
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: SPY_COLOR }}
              />
              <span
                className={`text-xs sm:text-sm ${showSpy ? "text-surface-200" : "text-surface-400"}`}
              >
                SPY
              </span>
            </button>
          )}
        </div>
      )}

      {/* Period Return Headline */}
      {enrichedData.length > 0 && visibleStrategiesList.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 sm:gap-5 mb-3">
          {visibleStrategiesList.map((s) => {
            const value = periodReturns[s.investment_id];
            if (value === undefined) return null;
            return (
              <div key={s.investment_id} className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                <span className="text-surface-400 text-xs sm:text-sm truncate max-w-[80px]">
                  {s.strategy_name}
                </span>
                <span className={`text-base sm:text-lg font-bold ${value >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {formatPercent(value)}
                </span>
              </div>
            );
          })}
          {showSpy && periodReturns["spy"] !== undefined && (
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: SPY_COLOR }} />
              <span className="text-surface-400 text-xs sm:text-sm">SPY</span>
              <span className={`text-base sm:text-lg font-bold ${periodReturns["spy"] >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {formatPercent(periodReturns["spy"])}
              </span>
            </div>
          )}
          <span className="text-xs text-surface-500 ml-auto">
            {timeRange === "CUSTOM" ? "Custom" : timeRanges.find(r => r.value === timeRange)?.label ?? timeRange}
          </span>
        </div>
      )}

      {/* Chart */}
      <div className="h-48 sm:h-64">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-red-400">
            {error}
          </div>
        ) : enrichedData.length === 0 ? (
          <div className="flex items-center justify-center h-full text-surface-500">
            No performance data available yet. Run your first execution to see
            performance.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={enrichedData}>
              <defs>
                {/* Gradient fills for each strategy */}
                {strategies.map((s) => (
                  <linearGradient
                    key={`gradient-${s.investment_id}`}
                    id={`gradient-${s.investment_id}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={s.color} stopOpacity={0.25} />
                    <stop offset="50%" stopColor={s.color} stopOpacity={0.08} />
                    <stop offset="100%" stopColor={s.color} stopOpacity={0.01} />
                  </linearGradient>
                ))}
                {/* SPY gradient */}
                <linearGradient id="gradient-spy" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={SPY_COLOR} stopOpacity={0.15} />
                  <stop offset="100%" stopColor={SPY_COLOR} stopOpacity={0.01} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#1e1e22"
                vertical={false}
              />
              <XAxis
                dataKey="date"
                stroke="#52525b"
                fontSize={10}
                tickFormatter={formatDate}
                interval={enrichedData.length <= 7 ? 0 : "preserveStartEnd"}
                axisLine={{ stroke: "#27272a" }}
                tickLine={false}
              />
              <YAxis
                orientation="right"
                stroke="#71717a"
                fontSize={10}
                tickFormatter={(value) =>
                  `${value >= 0 ? "+" : ""}${value.toFixed(0)}%`
                }
                domain={["auto", "auto"]}
                axisLine={false}
                tickLine={false}
                width={45}
              />
              <ReferenceLine
                y={0}
                stroke="#3f3f46"
                strokeWidth={1}
              />
              <Tooltip content={renderTooltip} />

              {/* Area fill under each visible strategy */}
              {visibleStrategiesList.map((s) => (
                <Area
                  key={`area-${s.investment_id}`}
                  type="monotone"
                  dataKey={s.investment_id}
                  fill={`url(#gradient-${s.investment_id})`}
                  stroke="none"
                  connectNulls
                  isAnimationActive={false}
                  tooltipType="none"
                />
              ))}

              {/* SPY area fill */}
              {showSpy && spyBenchmark.length > 0 && (
                <Area
                  key="area-spy"
                  type="monotone"
                  dataKey="spy"
                  fill="url(#gradient-spy)"
                  stroke="none"
                  connectNulls
                  isAnimationActive={false}
                  tooltipType="none"
                />
              )}

              {/* EMA lines (thin, slightly transparent) for each visible strategy */}
              {visibleStrategiesList.map((s) => (
                <Line
                  key={`ema-${s.investment_id}`}
                  type="monotone"
                  dataKey={`${s.investment_id}_ema`}
                  stroke={s.color}
                  strokeWidth={1}
                  strokeOpacity={0.4}
                  strokeDasharray="4 2"
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                  legendType="none"
                />
              ))}

              {/* SPY EMA */}
              {showSpy && spyBenchmark.length > 0 && (
                <Line
                  key="ema-spy"
                  type="monotone"
                  dataKey="spy_ema"
                  stroke={SPY_COLOR}
                  strokeWidth={1}
                  strokeOpacity={0.35}
                  strokeDasharray="4 2"
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                  legendType="none"
                />
              )}

              {/* Main strategy lines */}
              {visibleStrategiesList.map((s) => (
                <Line
                  key={s.investment_id}
                  type="monotone"
                  dataKey={s.investment_id}
                  stroke={s.color}
                  strokeWidth={2.5}
                  dot={(props) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const { cx, cy, index, payload } = props as any;
                    if (index === enrichedData.length - 1 && payload?.isLive) {
                      return (
                        <g key={`live-dot-${s.investment_id}`}>
                          <circle cx={cx} cy={cy} r={6} fill={s.color} opacity={0.3} className="animate-ping" />
                          <circle cx={cx} cy={cy} r={4} fill={s.color} stroke="#fff" strokeWidth={2} />
                        </g>
                      );
                    }
                    return null;
                  }}
                  name={s.strategy_name}
                  connectNulls
                  activeDot={{ r: 5, fill: s.color, stroke: "#fff", strokeWidth: 2 }}
                />
              ))}

              {/* SPY Benchmark Line */}
              {showSpy && spyBenchmark.length > 0 && (
                <Line
                  key="spy"
                  type="monotone"
                  dataKey="spy"
                  stroke={SPY_COLOR}
                  strokeWidth={2}
                  dot={(props) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const { cx, cy, index, payload } = props as any;
                    if (index === enrichedData.length - 1 && payload?.isLive) {
                      return (
                        <g key="live-dot-spy">
                          <circle cx={cx} cy={cy} r={6} fill={SPY_COLOR} opacity={0.3} className="animate-ping" />
                          <circle cx={cx} cy={cy} r={4} fill={SPY_COLOR} stroke="#fff" strokeWidth={2} />
                        </g>
                      );
                    }
                    return null;
                  }}
                  name="SPY"
                  connectNulls
                  strokeDasharray="5 5"
                  activeDot={{ r: 5, fill: SPY_COLOR, stroke: "#fff", strokeWidth: 2 }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
        </div>
      )}
    </div>
  );
}
